import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  viewChild,
} from '@angular/core';
import { MbaIcon, MbaIconName } from '../../../assignment/atoms/mba-icon/mba-icon';
import { bf } from '../../briefing-ui';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Right-hand edit drawer for one Briefing section. The section owns the draft and the save; this
 * is the frame: scrim, header, scrolling body, footer with Cancel / Save. Closes on ×, Cancel,
 * scrim or Esc, keeps focus inside, and returns focus to whatever opened it.
 */
@Component({
  selector: 'mbt-briefing-drawer',
  imports: [MbaIcon],
  template: `
    <div class="fixed inset-0 z-60 bg-(--mba-scrim) animate-[mba-fade_220ms_cubic-bezier(0.2,0,0,1)] motion-reduce:animate-none"
      (click)="cancel()"></div>
    <aside #panel role="dialog" aria-modal="true" [attr.aria-label]="mode() + ' ' + title()"
      class="mba fixed inset-y-0 right-0 z-61 flex w-[min(620px,100%)] flex-col bg-(--mba-surface) font-(family-name:--font-fallback) text-(--fg-1) shadow-[-20px_0_60px_rgba(18,6,43,0.22)] animate-[mba-dr_360ms_cubic-bezier(0.2,0,0,1)] motion-reduce:animate-none"
      (keydown)="onKeydown($event)">
      <header class="flex items-center gap-3 border-b border-(--border-subtle) px-[22px] py-4">
        <span [class]="bf.iconTile"><mba-icon [name]="icon()" size="size-5" /></span>
        <span class="flex min-w-0 flex-1 flex-col gap-1">
          <span [class]="bf.k">{{ mode() }}</span>
          <h2 class="m-0 font-(family-name:--font-display) text-[28px] leading-[0.9] font-bold tracking-[-0.02em]">{{ title() }}</h2>
        </span>
        <button type="button" aria-label="Close" [class]="bf.iconButton" (click)="cancel()"><mba-icon name="x" /></button>
      </header>
      <div data-drawer-body class="flex flex-1 flex-col gap-5 overflow-y-auto px-[22px] py-5">
        @if (helper()) {
          <span class="text-[13px] text-(--fg-3)">{{ helper() }}</span>
        }
        <ng-content />
      </div>
      <footer class="flex flex-wrap items-center gap-2.5 border-t border-(--border-subtle) bg-(--bg-alt) px-[22px] py-3.5">
        <span class="text-xs text-(--fg-3)">{{ note() }}</span>
        <span class="flex-1"></span>
        <button type="button" [class]="bf.btnOutline" (click)="cancel()">Cancel</button>
        @if (canSave()) {
          <button type="button" [class]="bf.btnPrimary" [disabled]="saving()" (click)="save.emit()">
            <mba-icon name="check" size="size-4" [strokeWidth]="2.25" />{{ saving() ? 'Saving…' : 'Save' }}
          </button>
        }
      </footer>
    </aside>
  `,
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingDrawer implements OnDestroy {
  protected readonly bf = bf;

  readonly icon = input.required<MbaIconName>();
  readonly title = input.required<string>();
  /** "Edit" or "Set up". */
  readonly mode = input('Edit');
  readonly helper = input('');
  /** Left side of the footer, e.g. a lock reason. */
  readonly note = input('');
  readonly saving = input(false);
  /** False for a read-only drawer (a locked section), which only offers Cancel. */
  readonly canSave = input(true);

  readonly closed = output<void>();
  readonly save = output<void>();

  private readonly panel = viewChild.required<ElementRef<HTMLElement>>('panel');
  private readonly opener = inject(ElementRef).nativeElement.ownerDocument?.activeElement as HTMLElement | null;

  constructor() {
    afterNextRender(() => {
      const panel = this.panel().nativeElement;
      // Sections that open with a target field focus it themselves; otherwise start on the first field.
      if (!panel.contains(document.activeElement)) {
        const body = panel.querySelector<HTMLElement>('[data-drawer-body]');
        // First control in reading order, skipping rich-text toolbars so the text itself gets focus.
        const first = [
          ...(body?.querySelectorAll<HTMLElement>(
            'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [contenteditable="true"]',
          ) ?? []),
        ].find((el) => !el.closest('[class*="MenuBar"]'));
        (first ?? panel.querySelector<HTMLElement>(FOCUSABLE))?.focus();
      }
    });
  }

  ngOnDestroy(): void {
    this.opener?.focus?.();
  }

  protected cancel(): void {
    if (!this.saving()) {
      this.closed.emit();
    }
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !document.querySelector('[data-mba-modal]')) {
      event.stopPropagation();
      this.cancel();
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }
    const items = [...this.panel().nativeElement.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (!items.length) {
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
